import { useVoiceAt } from '@/features/voice-assistant/workspace/voicePlacement';
import {
  SessionWorkPanelHeader as SessionWorkPanelHeaderView,
  SessionWorkVoiceSidebarSync as SessionWorkVoiceSidebarSyncView,
  type SessionWorkPanelHeaderProps as SessionWorkPanelHeaderViewProps,
} from '@heidi/agents';
import { clearLibraryDocumentPreview } from '@heidi/evidence';
import type { NavigatePort } from '@heidi/runtime/shell';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useSessionWorkPanelEvidenceChrome } from '@/hooks/useSessionWorkPanelEvidenceChrome';
import { useQuerySessionById } from '@heidi/session/data';
import { ProjectHeaderActions } from './ProjectHeaderActions';
import { ProjectTitleCluster } from './ProjectTitleCluster';
import { SessionWorkProjectSelector } from './SessionWorkProjectSelector';

/**
 * Keep the session RHS open while Voice occupies the Work slot, bound to the
 * app's Voice placement. Mount outside the panel's `isOpen` gate — otherwise
 * Voice cannot open a closed sidebar.
 */
export function SessionWorkVoiceSidebarSync({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}) {
  const voiceInWorkSlot = useVoiceAt('session-rhs');
  return (
    <SessionWorkVoiceSidebarSyncView
      setOpen={setOpen}
      voiceInWorkSlot={voiceInWorkSlot}
    />
  );
}

type SessionWorkPanelHeaderProps = SessionWorkPanelHeaderViewProps & {
  /** Work's own project pick; the Evidence chrome hands it on while Work owns the panel. */
  onSelectProject?: (projectId: string) => void;
};

/**
 * Binds the session Work panel's header to the app: the Evidence chat chrome
 * (Chat's sidebar state and queries), the session record's patient profile when
 * the host has none, the route for Open full screen, the document library's
 * preview clear, and the title cluster, actions and project selector bound to
 * the chat-list mutations, flags and routine reads.
 */
export const SessionWorkPanelHeader = ({
  onSelectProject,
  ...props
}: SessionWorkPanelHeaderProps) => {
  const { sessionId, associatedPatientProfileId, evidenceChatId } = props;
  const router = useRouter();
  const evidenceChrome = useSessionWorkPanelEvidenceChrome({
    sessionId,
    evidenceChatId,
    onSelectProject,
  });
  const { data: sessionPatientProfileId } = useQuerySessionById({
    sessionId,
    select: (session) => session?.patient_profile?.id ?? null,
  });
  const onNavigate = useCallback<NavigatePort>(
    (href) => {
      void router.push(href);
    },
    [router]
  );

  return (
    <SessionWorkPanelHeaderView
      {...props}
      associatedPatientProfileId={associatedPatientProfileId ?? sessionPatientProfileId}
      evidenceChrome={evidenceChrome}
      onNavigate={onNavigate}
      clearLibraryDocumentPreview={clearLibraryDocumentPreview}
      TitleCluster={ProjectTitleCluster}
      HeaderActions={ProjectHeaderActions}
      ProjectSelector={SessionWorkProjectSelector}
    />
  );
};
